import React, { useState, useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { Label, Input, Button } from "reactstrap";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";
import { checkPhoneNumber } from "../../../store/actions/authActions";
import ReusableModal from "../../../components/Modals/ReusableModal";

const RESEND_COOLDOWN = 60; // seconds

const PhoneVerificationModal = ({ isOpen, onVerified, onClose }) => {
  const dispatch = useDispatch();
  const [phoneNumber, setPhoneNumber] = useState("");
  const [otp, setOtp] = useState(false);
  const [confirmation, setConfirmation] = useState(null);
  const [verificationCode, setVerificationCode] = useState("");
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const timerRef = useRef(null);

  // Start resend cooldown timer
  const startCountdown = () => {
    setCountdown(RESEND_COOLDOWN);
    timerRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  // Clean up timer on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  const resetToPhoneStep = () => {
    setOtp(false);
    setVerificationCode("");
    setConfirmation(null);
    if (timerRef.current) clearInterval(timerRef.current);
    setCountdown(0);
  };

  const handleClose = () => {
    setPhoneNumber("");
    resetToPhoneStep();
    onClose();
  };

  const handleSendOtp = async (e) => {
    e.preventDefault();
    if (!phoneNumber) return toast.warning("Please enter phone number");
    setSending(true);
    try {
      await dispatch(
        checkPhoneNumber(
          phoneNumber,
          (confirmationResult) => {
            setConfirmation(confirmationResult);
            setOtp(true);
            startCountdown();
          },
          () => {
            // onError — reset so the recaptcha div is re-rendered
            resetToPhoneStep();
          },
        ),
      );
    } catch (error) {
      toast.warning(error.message);
      resetToPhoneStep();
    } finally {
      setSending(false);
    }
  };

  const handleResend = async () => {
    if (countdown > 0) return;
    resetToPhoneStep();
    // Small timeout so the recaptcha-container is back in the DOM
    setTimeout(() => {
      setSending(true);
      dispatch(
        checkPhoneNumber(
          phoneNumber,
          (confirmationResult) => {
            setConfirmation(confirmationResult);
            setOtp(true);
            startCountdown();
            setSending(false);
          },
          () => {
            resetToPhoneStep();
            setSending(false);
          },
        ),
      );
    }, 100);
  };

  const handleVerify = async () => {
    if (!verificationCode) return toast.warning("Please enter the OTP");
    if (!confirmation) return toast.warning("No active verification session. Please resend the code.");
    setVerifying(true);
    try {
      await confirmation.confirm(verificationCode);
      toast.success("Successfully verified");
      onVerified(phoneNumber);
      handleClose();
    } catch (error) {
      // Firebase error codes for bad/expired codes
      if (
        error.code === "auth/invalid-verification-code" ||
        error.code === "auth/code-expired"
      ) {
        toast.warning(
          error.code === "auth/code-expired"
            ? "The SMS code has expired. Please tap Resend to get a new one."
            : "Invalid code. Please check the SMS and try again.",
        );
      } else {
        toast.warning(error.message);
      }
    } finally {
      setVerifying(false);
    }
  };

  return (
    <ReusableModal isOpen={isOpen} toggle={handleClose} size="sm" footer={null}>
      {otp ? (
        <div>
          <Label className="custom-label">Enter OTP</Label>
          <Input
            type="text"
            placeholder="Enter OTP.."
            className="custom-input"
            value={verificationCode}
            onChange={(e) => setVerificationCode(e.target.value)}
            disabled={verifying}
          />
          <div className="text-right mt-4">
            <Button color="danger" size="sm" onClick={handleClose} disabled={verifying}>
              Cancel
            </Button>
            <Button
              color="primary"
              size="sm"
              onClick={handleVerify}
              disabled={verifying || !verificationCode}
            >
              {verifying ? "Verifying…" : "Verify"}
            </Button>
          </div>
          <p
            onClick={countdown > 0 ? undefined : handleResend}
            className={`mt-2 ${countdown > 0 ? "text-muted" : "pointer"}`}
            style={{ cursor: countdown > 0 ? "default" : "pointer" }}
          >
            {countdown > 0 ? `Resend in ${countdown}s` : "Resend"}
          </p>
        </div>
      ) : (
        <div>
          <Label className="custom-label">Enter Phone Number</Label>
          <PhoneInput
            defaultCountry="US"
            placeholder="Enter phone number"
            value={phoneNumber}
            onChange={setPhoneNumber}
            className="mb-4"
            disabled={sending}
          />
          {/* reCAPTCHA mounts here — must be present in the DOM before sending OTP */}
          <div id="recaptcha-container" className="recaptcha-container" />
          <div className="text-right mt-4">
            <Button color="danger" size="sm" onClick={handleClose} disabled={sending}>
              Cancel
            </Button>
            <Button
              color="primary"
              size="sm"
              onClick={handleSendOtp}
              disabled={sending || !phoneNumber}
            >
              {sending ? "Sending…" : "Send OTP"}
            </Button>
          </div>
        </div>
      )}
    </ReusableModal>
  );
};

export default PhoneVerificationModal;
